import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import WheelsDigitalTimePicker from '../WheelsDigitalTimePicker/WheelsDigitalTimePicker.vue'
import { time12hTo24hString, time24hTo12hString } from '@/utils/timeStrings'
import { generateShortUUID } from '@/utils/id'

// Props

export default /*#__PURE__*/_defineComponent({
  __name: 'WheelsTimeInput',
  props: {
    value: null,
    label: null,
    placeholder: null,
    disabled: { type: Boolean },
    error: { type: Boolean },
    variant: { default: 'default' }
  },
  emits: ["change"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'change', value: string | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value?: string, label?: string, placeholder?: string, disabled?: boolean, error?: boolean, variant: 'blue-gray' | 'default' };



// Emits


// Reactive state
const isDropdownOpen = ref(false)
const uuid = ref('')

// Template refs
const timeInputContainer = ref<HTMLDivElement>()
const timeInputDropdown = ref<HTMLDivElement>()
const timeInputElement = ref<HTMLInputElement>()
const digitalTimePicker = ref<InstanceType<typeof WheelsDigitalTimePicker>>()

// Computed properties
const isoValue = computed(() => {
  return time12hTo24hString(props.value || '')
})

watch(isDropdownOpen, (open) => {
  if (open) {
    nextTick(() => {
      setDropdownMaxHeight()
    })
  }
})

// Lifecycle
onMounted(() => {
  uuid.value = generateShortUUID()
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Methods

// TODO: Consider using Vue teleport for the dropdown
function toggleDropdown(): void {
  if (props.disabled) return

  if (!isDropdownOpen.value) {
    isDropdownOpen.value = true
    nextTick(() => setDropdownMaxHeight())
  } else {
    isDropdownOpen.value = false
  }
}

function closeDropdown(): void {
  if (isDropdownOpen.value) {
    isDropdownOpen.value = false
  }
}

// If we're clicking within the dropdown or picker, keep it open
// Otherwise, close it
function handleClickOutside(event: MouseEvent): void {
  const target = event.target as HTMLElement

  const clickedInsideContainer =
    timeInputContainer.value?.contains(target) ?? false
  const clickedInsideDropdown =
    timeInputDropdown.value?.contains(target) ?? false
  const clickedInsidePicker =
    digitalTimePicker.value?.$el.contains(target) ?? false

  const shouldClose =
    !clickedInsideContainer && !clickedInsideDropdown && !clickedInsidePicker

  if (shouldClose) {
    closeDropdown()
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    toggleDropdown()
    return
  }
  if (isDropdownOpen.value && event.key === 'Escape') {
    event.preventDefault()
    closeDropdown()
  }
}

function handleDigitalPickerChange(isoTime: string | null): void {
  const displayTime = time24hTo12hString(isoTime || '')
  emit('change', displayTime)
}

/**
 * Dynamically calculates and sets the maximum height of the dropdown
 * so it never overflows the viewport.
 * By measuring the input's position with getBoundingClientRect(),
 * we know exactly how much space is available below it.
 *
 * This ensures the dropdown always fits within the viewport,
 * while still enforcing a reasonable minimum height.
 */
function setDropdownMaxHeight(): void {
  const DROPDOWN_PADDING_PX = 100 // px buffer
  const MIN_DROPDOWN_HEIGHT_PX = 200 // Minimum height of the dropdown
  if (!timeInputDropdown.value || !timeInputContainer.value) return

  const inputRect = timeInputContainer.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  const spaceBelow = viewportHeight - inputRect.bottom
  const maxHeight = Math.max(
    spaceBelow - DROPDOWN_PADDING_PX,
    MIN_DROPDOWN_HEIGHT_PX,
  )

  timeInputDropdown.value.style.maxHeight = `${maxHeight}px`
}

return { __sfc: true,props, emit, isDropdownOpen, uuid, timeInputContainer, timeInputDropdown, timeInputElement, digitalTimePicker, isoValue, toggleDropdown, closeDropdown, handleClickOutside, handleKeyDown, handleDigitalPickerChange, setDropdownMaxHeight, WheelsDigitalTimePicker }
}

})